import _ from "lodash";
import { convertDegreesInSegment } from "./unitHelpers";
import {
  BULLSEYE_FULL_FUSE_MAX_C,
  BULLSEYE_FULL_FUSE_MIN_C,
  BULLSEYE_STRIKER_TYPES,
  GOLD_HOLD_MIN_MINUTES,
  GOLD_HOLD_TEMP_MAX_C,
  GOLD_HOLD_TEMP_MIN_C,
} from "./strikers";
import type {
  Project,
  Firing,
  Segment,
  GlassData,
  Material,
  ProjectWarning,
  StrikerType,
} from "../types";

export type Warning = ProjectWarning;

const maxtemp = 899;
const maxtemp_for_incompatible = 815;
const maxtemp_for_incompatible_too_long = 815;
const max_incompatible_length = 15;

const materialsThatContainIncompatibilities = (
  instabilityType: string,
  materials: Record<string, Material> | undefined,
  glassData: GlassData | null | undefined
): (Material & { glass_reference: string })[] => {
  if (!materials || !glassData?.reactions) return [];
  return Object.values(materials).filter((material) => {
    if (!material.glass_reference) return false;
    const codeRoot = material.glass_reference.split("-")[0];
    const reaction =
      glassData.reactions[codeRoot] || glassData.reactions[material.glass_reference];
    return reaction?.instability_type === instabilityType;
  }) as (Material & { glass_reference: string })[];
};

export const getStrikerType = (
  glassReference: string,
  glassData: GlassData | null | undefined,
  projectGlass: string | undefined
): StrikerType | null => {
  if (!glassReference) return null;
  const codeRoot = glassReference.split("-")[0];
  const reaction =
    glassData?.reactions?.[codeRoot] || glassData?.reactions?.[glassReference];
  if (reaction?.striker_type) return reaction.striker_type;
  if (projectGlass === "Bullseye") {
    return BULLSEYE_STRIKER_TYPES[codeRoot] ?? null;
  }
  return null;
};

export const projectHasGoldHold = (
  firings: Firing[] | undefined,
  segments: Segment[] | undefined
): boolean => {
  if (!firings || !segments) return false;
  for (const firing of firings) {
    const firingSegments = segments.filter((s) => s?.firing_id === firing.id);
    for (const seg of firingSegments) {
      const c = convertDegreesInSegment(seg, "celsius");
      const tempC = Number(c.temperature);
      const holdMin = Number(seg.hold);
      if (
        Number.isFinite(tempC) &&
        Number.isFinite(holdMin) &&
        tempC >= GOLD_HOLD_TEMP_MIN_C &&
        tempC <= GOLD_HOLD_TEMP_MAX_C &&
        holdMin >= GOLD_HOLD_MIN_MINUTES
      ) {
        return true;
      }
    }
  }
  return false;
};

export const projectHasFullFuse = (
  firings: Firing[] | undefined,
  segments: Segment[] | undefined
): boolean => {
  if (!firings || !segments) return false;
  for (const firing of firings) {
    const firingSegments = segments.filter((s) => s?.firing_id === firing.id);
    for (const seg of firingSegments) {
      const tempC = Number(convertDegreesInSegment(seg, "celsius").temperature);
      if (Number.isFinite(tempC) && tempC >= BULLSEYE_FULL_FUSE_MIN_C) {
        return true;
      }
    }
  }
  return false;
};

export const getProjectWarnings = (
  project: Project,
  firings: Firing[],
  segments: Segment[],
  glassData: GlassData | null | undefined
): Warning[] => {
  const warnings: Warning[] = [];

  // High temperature warning — check each firing's segments
  if (firings && segments) {
    for (const firing of firings) {
      const firingSegments = segments.filter((s) => s?.firing_id === firing.id);
      for (const seg of firingSegments) {
        const tempC = Number(convertDegreesInSegment(seg, "celsius").temperature);
        if (tempC > maxtemp) {
          warnings.push({
            severity: "warning",
            title: "A Firing has a suspiciously high temperature",
            detail: `The firing called: '${firing.name}' contains a segment with a really high target temperature (above ${maxtemp}C)`,
          });
          break;
        }
      }
    }
  }

  // Multiple glass types warning
  const glassTypesInProject = project.materials
    ? [...new Set(Object.values(project.materials).map((m) => m.glass).filter(Boolean))]
    : [];

  if (glassTypesInProject.length > 1) {
    warnings.push({
      severity: "warning",
      title: "This project uses more than a single glass type",
      detail:
        "Fusing incompatible glass types is not sensible. If the glass types have different CoE values, then internal stresses mean that the piece might fail, even some time after coming out of the kiln.",
    });
  }

  // Glass type mismatch warning
  const foreignGlassTypes = _.filter(
    glassTypesInProject,
    (g) => g !== project.glass
  );
  if (foreignGlassTypes.length > 0) {
    warnings.push({
      severity: "warning",
      title:
        "This project is set to use a different material type than the materials themselves",
      detail: `This project has a glass type of ${project.glass}, but contains materials of the following type(s): ${foreignGlassTypes.join(",")}. This means that things like process temperature indicators on the graph will probably be wrong.`,
    });
  }

  // Cadmium at high temperature warning
  const incompatibleMaterials = materialsThatContainIncompatibilities(
    "CADMIUM-BEARING",
    project.materials,
    glassData
  );
  if (incompatibleMaterials.length > 0 && segments) {
    const maxSegmentTempC = segments.reduce(
      (max, seg) =>
        Math.max(max, Number(convertDegreesInSegment(seg, "celsius").temperature)),
      0
    );
    if (maxSegmentTempC > maxtemp_for_incompatible) {
      warnings.push({
        severity: "warning",
        title:
          "This project contains glass which is unstable at high temperatures",
        detail:
          `Bullseye glass that contains Cadmium can become unstable if exposed to high temperatures for too long. The glass is tested for up to 3 firings of not more than ${max_incompatible_length} minutes at or below ${maxtemp_for_incompatible_too_long} degrees Celsius. Past this point, these glasses may Opalize, or in extreme circumstances, shift CoE. The following material codes in this project are affected: ` +
          incompatibleMaterials.map((m) => m.glass_reference).join(","),
      });
    }
  }

  // Striker / tint-shift warnings — only meaningful for Bullseye projects.
  // Gold strikers need a hold around 663°C to strike. Cadmium/selenium and
  // other strikers strike on the way through a full fuse, so they're satisfied
  // by any firing that reaches the full-fuse range. Tint/shift glasses are a
  // softer info entry — they may not fully develop without a gold hold.
  if (project.glass === "Bullseye" && project.materials) {
    const strikerMaterials: { material: Material; type: StrikerType }[] = [];
    for (const material of Object.values(project.materials)) {
      if (!material.glass_reference) continue;
      const t = getStrikerType(material.glass_reference, glassData, project.glass);
      if (t) strikerMaterials.push({ material, type: t });
    }

    if (strikerMaterials.length > 0) {
      const goldStrikers = strikerMaterials.filter((s) => s.type === "GOLD");
      const tintStrikers = strikerMaterials.filter((s) => s.type === "TINT_SHIFT");
      const otherStrikers = strikerMaterials.filter(
        (s) => s.type !== "GOLD" && s.type !== "TINT_SHIFT"
      );

      const hasGoldHold = projectHasGoldHold(firings, segments);
      const hasFullFuse = projectHasFullFuse(firings, segments);

      if (goldStrikers.length > 0 && !hasGoldHold) {
        const codes = goldStrikers
          .map((s) => s.material.glass_reference)
          .join(", ");
        warnings.push({
          severity: "warning",
          title: "Striker glass without a gold hold",
          detail:
            `Bullseye gold striker glasses develop their final colour only when fired through a hold around ${GOLD_HOLD_TEMP_MIN_C}–${GOLD_HOLD_TEMP_MAX_C}°C for at least ${GOLD_HOLD_MIN_MINUTES} minutes (Bullseye's spec is 663°C for 2 hours). None of this project's firings includes such a hold, so these colours are likely to come out wrong (pale, muddy, or undeveloped). The following material codes are affected: ` +
            codes,
        });
      }

      if (otherStrikers.length > 0 && !hasFullFuse) {
        const codes = otherStrikers
          .map((s) => s.material.glass_reference)
          .join(", ");
        warnings.push({
          severity: "warning",
          title: "Striker glass without a full fuse",
          detail:
            `Bullseye cadmium/selenium striker glasses develop their final colour only when taken through a full fuse (around ${BULLSEYE_FULL_FUSE_MIN_C}–${BULLSEYE_FULL_FUSE_MAX_C}°C). None of this project's firings reaches a full fuse, so these colours are likely to come out wrong (pale, muddy, or undeveloped). The following material codes are affected: ` +
            codes,
        });
      }

      if (tintStrikers.length > 0 && !hasGoldHold) {
        const codes = tintStrikers
          .map((s) => s.material.glass_reference)
          .join(", ");
        warnings.push({
          severity: "info",
          title: "Tint/shift glass may not fully develop",
          detail:
            `Bullseye tint and shift glasses tend to develop saturation during a hold around ${GOLD_HOLD_TEMP_MIN_C}–${GOLD_HOLD_TEMP_MAX_C}°C for at least ${GOLD_HOLD_MIN_MINUTES} minutes (Bullseye's gold-hold spec is 663°C for 2 hours). None of this project's firings includes such a hold, so the following materials' colours may not fully develop: ` +
            codes,
        });
      }
    }
  }

  return warnings;
};
